var _effectorFileName = "/src/page-content/payment/components/payment-form/payment-form.tsx";
import { useState } from 'react';
import Link from 'next/link';
import { ServiceVariant } from '@/pages/api/services/types';
import type { Teacher } from '@/pages/api/teachers/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUnit } from 'effector-react';
import { getFileInfo } from 'orval';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ComboboxServices } from '@/page-content/payment/components/combobox-services';
import { ComboboxTeachers } from '@/page-content/payment/components/combobox-teachers';
import { $servicesList, $teachersList } from '@/shared/api/payment/api';
import PaymentSystemsIcon from '@/shared/components/icons/payment/systems-icon';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';
import { $isPromocodeAccepted, paymentDataSent, promocodeDataSent } from './model';
import { paymentFormSchema } from './schema';
import { PaymentData } from './types';
export const PaymentForm = () => {
  const {
    sendPaymentData,
    sendPromocodeData,
    isPromocodeAccepted,
    servicesData,
    teachersData
  } = useUnit({
    sendPaymentData: paymentDataSent,
    sendPromocodeData: promocodeDataSent,
    isPromocodeAccepted: $isPromocodeAccepted,
    servicesData: $servicesList,
    teachersData: $teachersList
  });
  const [promocodeValue, setPromocodeValue] = useState('');
  const [isLoading, setLoading] = useState(false);
  const paymentForm = useForm<z.infer<typeof paymentFormSchema>>({
    resolver: zodResolver(paymentFormSchema),
    mode: 'onChange',
    defaultValues: {
      serviceCategory: '',
      service: {
        lessonPrice: '',
        lessonType: ''
      },
      teacher: {
        name: '',
        surname: '',
        patronymicName: '',
        taxNumber: '',
        tel: ''
      },
      client: {
        name: '',
        surname: '',
        email: ''
      },
      terms: {
        isAgreeWithPublicOffer: false,
        isAgreeWithPaymentRules: false,
        isAgreeWithPersonalDataProcessing: false,
        isPromocodeAccepted: null,
        isForeignCard: false
      }
    }
  });
  const {
    formState,
    setValue,
    handleSubmit,
    getFieldState,
    getValues
  } = paymentForm;
  const onSelectTeachersChange = (teacher: Teacher) => {
    setValue('teacher', teacher, {
      shouldValidate: true
    });
  };
  const onSelectServicesChange = (serviceCategory: string, variant: ServiceVariant) => {
    setValue('serviceCategory', serviceCategory, {
      shouldValidate: true
    });
    setValue('service', variant, {
      shouldValidate: true
    });
  };
  const checkPromocodeExistanceHandler = () => {
    setLoading(true);
    sendPromocodeData({
      promocode: promocodeValue.toUpperCase().trim()
    });
    setLoading(false);
  };
  const onSubmit = (values: z.infer<typeof paymentFormSchema>) => {
    const {
      client,
      teacher,
      serviceCategory,
      service,
      terms
    } = values;
    const paymentData: PaymentData = {
      serviceCategory,
      teacher,
      service,
      client,
      isPromocodeAccepted: isPromocodeAccepted,
      isForeignCard: terms.isForeignCard
    };
    sendPaymentData(paymentData);
  };
  return <Form {...paymentForm}>
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-5xl space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={paymentForm.control} name="teacher" render={() => <FormItem className="flex flex-col">
                <FormLabel>Учитель</FormLabel>

                <FormControl>
                  <ComboboxTeachers teachersList={teachersData ? teachersData.filter(teacher => teacher.isVisibleOnPaymentPage) : []} onOptionPick={onSelectTeachersChange} />
                </FormControl>

                <FormMessage className="font-normal" />
              </FormItem>} />

          <FormField control={paymentForm.control} name="service" render={() => <FormItem className="flex flex-col">
                <FormLabel>Тип урока</FormLabel>

                <FormControl>
                  <ComboboxServices servicesList={servicesData} onOptionPick={onSelectServicesChange} />
                </FormControl>

                <FormMessage className="font-normal" />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={paymentForm.control} name="client.name" render={({
          field
        }) => <FormItem className="flex flex-col">
                <FormLabel>Имя</FormLabel>

                <FormControl>
                  <Input {...field} placeholder="Имя" className="text-base capitalize sm:text-sm" />
                </FormControl>

                <FormMessage className="font-normal" />
              </FormItem>} />

          <FormField control={paymentForm.control} name="client.surname" render={({
          field
        }) => <FormItem className="flex flex-col">
                <FormLabel>Фамилия</FormLabel>

                <FormControl>
                  <Input {...field} placeholder="Фамилия" className="text-base capitalize sm:text-sm" />
                </FormControl>

                <FormMessage className="font-normal" />
              </FormItem>} />
        </div>

        <FormField control={paymentForm.control} name="client.email" render={({
        field
      }) => <FormItem className="flex flex-col">
              <FormLabel>E-mail</FormLabel>

              <FormControl>
                <Input {...field} placeholder="E-mail" className="text-base sm:text-sm" />
              </FormControl>

              <FormMessage className="font-normal" />
            </FormItem>} />

        <div className={cn('max-w-lg space-y-4 rounded-lg border p-3 transition-all  duration-300 hover:bg-accent/50', {
        'border-brand-accent-normal bg-accent/50 dark:border-brand-accent-normal dark:bg-accent/50': getValues('terms').isAgreeWithPublicOffer && getValues('terms').isAgreeWithPaymentRules && getValues('terms').isAgreeWithPersonalDataProcessing
      })}>
          <FormField control={paymentForm.control} name="terms.isAgreeWithPublicOffer" render={({
          field
        }) => <div>
                <FormItem className="flex gap-2">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={isChecked => field.onChange(isChecked)} className="data-[state=checked]:border-brand-accent-dark data-[state=checked]:bg-brand-accent-dark data-[state=checked]:text-white dark:data-[state=checked]:border-brand-accent-normal dark:data-[state=checked]:bg-brand-accent-normal" />
                  </FormControl>

                  <FormLabel className="!mt-0">
                    <span>Принимаю </span>
                    <Button variant="link" size="link-sm" className="text-muted-foreground" aria-label="Прочитать условия оферты">
                      <Link href="/public-offer" aria-label="Прочитать условия оферты">
                        условия оферты
                      </Link>
                    </Button>
                  </FormLabel>
                </FormItem>

                <FormMessage className="font-normal" />
              </div>} />

          <FormField control={paymentForm.control} name="terms.isAgreeWithPaymentRules" render={({
          field
        }) => <div>
                <FormItem className="flex gap-2">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={isChecked => field.onChange(isChecked)} className="data-[state=checked]:border-brand-accent-dark data-[state=checked]:bg-brand-accent-dark data-[state=checked]:text-white dark:data-[state=checked]:border-brand-accent-normal dark:data-[state=checked]:bg-brand-accent-normal" />
                  </FormControl>

                  <FormLabel className="!mt-0">
                    Подтверждаю, что ознакомлен(-а) с{' '}
                    <Button variant="link" size="link-sm" className="text-muted-foreground" aria-label="Прочитать правила оплаты">
                      <Link href="/payment-rules" aria-label="Прочитать правила оплаты">
                        правилами оплаты
                      </Link>
                    </Button>
                  </FormLabel>
                </FormItem>

                <FormMessage className="font-normal" />
              </div>} />

          <FormField control={paymentForm.control} name="terms.isAgreeWithPersonalDataProcessing" render={({
          field
        }) => <div>
                <FormItem className="flex items-start gap-3">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={isChecked => field.onChange(isChecked)} className="data-[state=checked]:border-brand-accent-dark data-[state=checked]:bg-brand-accent-dark data-[state=checked]:text-white dark:data-[state=checked]:border-brand-accent-normal dark:data-[state=checked]:bg-brand-accent-normal" />
                  </FormControl>

                  <FormLabel className="!mt-0">
                    Подтверждаю, что согласен(-а) на{' '}
                    <Button variant="link" size="link-sm" className="mt-1 text-wrap text-left text-muted-foreground" onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}>
                      <Link href="/agreement" target="_blank" className="text-sm font-normal leading-tight">
                        обработку персональных данных
                      </Link>
                    </Button>
                  </FormLabel>
                </FormItem>

                <FormMessage className="font-normal" />
              </div>} />

          <FormField control={paymentForm.control} name="terms.isForeignCard" render={({
          field
        }) => <div>
                <FormItem className="flex items-start gap-2">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={isChecked => field.onChange(isChecked)} className="data-[state=checked]:border-brand-accent-dark data-[state=checked]:bg-brand-accent-dark data-[state=checked]:text-white dark:data-[state=checked]:border-brand-accent-normal dark:data-[state=checked]:bg-brand-accent-normal" />
                  </FormControl>

                  <FormLabel className="!mt-0 leading-4">Буду оплачивать картой Белорусского банка</FormLabel>
                </FormItem>
              </div>} />
        </div>

        <div>
          <FormItem>
            <FormLabel htmlFor="promocode" className="!mt-0">
              Промокод
            </FormLabel>

            <div className="flex items-center gap-4">
              <FormControl>
                <Input id="promocode" className="text-base sm:max-w-[30%] sm:text-sm" value={promocodeValue} disabled={!!isPromocodeAccepted} onChange={e => setPromocodeValue(e?.target.value)} />
              </FormControl>

              {!isPromocodeAccepted && <Button type="button" className="!mt-0" disabled={!promocodeValue.length || isLoading} onClick={checkPromocodeExistanceHandler} aria-label="Применить промокод">
                  Применить
                </Button>}
            </div>
          </FormItem>

          {isPromocodeAccepted === false && <FormMessage className="font-normal">Такого промокода не существует</FormMessage>}
          {isPromocodeAccepted === true && <FormMessage className="font-normal text-green-600">Промокод применен</FormMessage>}
        </div>

        <PaymentSystemsIcon className="!mb-10 h-6 w-auto text-black/80 dark:text-white sm:!mb-20 sm:h-8" />

        <div>
          <Button disabled={!formState.isValid || formState.isSubmitting} variant="cta" type="submit" className="mx-auto block tracking-wide" aria-label="Оплатить">
            Оплатить
          </Button>
        </div>
      </form>
    </Form>;
};